/**
 * The fab world feature (M8 MC-c5b): gives the Outfitting tab's fab rows the live world through the provider of
 * `ui/models/fabWorld.ts`, read by `ui/models/view.ts` on each view build. No `UiActions` or `CareerPort` member.
 *
 * - **Mk III geography** (D-493): the strategic client's live view of the local system (`systemView.systemId ===
 *   manifest.systemId`, the career runtime's `geographyView` rule), so the Fabricate row warns before the click.
 *   No such view keeps the any-station rule, as the runtime does.
 * - **The shortage card** (D-499, the D-503/D-515 sources): the Reach state and galaxy from `ctx.reach`; views =
 *   every system view the client delivered since init (any age) plus its current one; covered = the
 *   client's `manifest.systemId` ∪ systems with a built player-corp station (`playerStationViews`, re-read when the
 *   local system changes). The pilot's system is `manifest.systemId`.
 * - **Remote berth pull** (D-510): the fab feature's `fabRemoteWorld` over the same Reach runtime and the client's
 *   cached view, exactly what `fab.deliver` passes, so the rows count what a delivery would draw.
 *
 * Without a Reach runtime (M2's own career) nothing registers and the rows read as before. The client is read
 * lazily (the strategic link provides it after earlier features' init). UI reads only, never per frame.
 */
import { FEATURE_ORDER, type GameContext, type GameFeature } from '../../app/feature'
import { logger } from '../../core/logger'
import type { ReachCore } from '../../reach/runtime/types'
import type { StrategicClient } from '../../strategic/client'
import { strategicMarketFor, type StrategicMarket } from '../../strategic-link/market'
import type { PriceSource } from '../../trade-intel/priceLog'
import { registerFabWorld, type FabWorldProvider, type StationWorld } from '../../ui/models/fabWorld'
import { fabRemoteWorld } from '../fab/feature'

/** After the station screens (710): UI-side, and every source is read lazily anyway. */
const ORDER = FEATURE_ORDER.station + 1

/** What the provider follows of the strategic client. */
export type FabWorldClient = Pick<StrategicClient, 'manifest' | 'systemView' | 'onSystemView'> & Partial<Pick<StrategicClient, 'playerStationViews'>>

/** What the provider reads of the Reach runtime. */
export type FabWorldReach = Pick<ReachCore, 'galaxy' | 'state' | 'layoutOf'> & { readonly session: { readonly layout: Parameters<typeof fabRemoteWorld>[2] } }

export interface FabWorldSource {
  readonly options: FabWorldProvider
  /** Starts following the client's views when it exists (at init, so views delivered before the first read count). */
  follow(): void
  dispose(): void
}

/**
 * The provider over a lazily read Reach and strategic client (exported for the tests). `market` (K12): the strategic
 * market the career runtime trades through, for the Repair tab's `linked` signal; absent or null: not linked.
 */
export function createFabWorldSource(deps: {
  reach(): FabWorldReach | null
  client(): FabWorldClient | null
  market?(): Pick<StrategicMarket, 'resolve'> | null
}): FabWorldSource {
  const views = new Map<string, PriceSource>()
  const keepView = (v: PriceSource | null): void => {
    if (v !== null) views.set(v.systemId, { systemId: v.systemId, stations: v.stations })
  }
  let attached: FabWorldClient | null = null
  let offView: (() => void) | null = null
  let ownSystems: ReadonlySet<string> = new Set()
  let ownFor: string | null = null
  let disposed = false

  /** Follows the client's views once it exists (or a new one). */
  const attach = (c: FabWorldClient): void => {
    if (c === attached) return
    offView?.()
    attached = c
    views.clear()
    ownSystems = new Set()
    ownFor = null
    offView = c.onSystemView((v) => keepView(v))
  }

  /** Re-reads the player-corp station systems when the local system changes (async; the next build sees them). */
  const refreshOwn = (c: FabWorldClient, here: string): void => {
    if (ownFor === here) return
    ownFor = here
    const read = c.playerStationViews
    if (read === undefined) return
    read().then(
      (own) => {
        if (disposed || c !== attached) return
        const next = new Set<string>()
        for (const st of own) if (st.station !== null) next.add(st.systemId)
        ownSystems = next
      },
      (err: unknown) => {
        logger.debug('Fab world: no player station views', err)
      },
    )
  }

  /** The runtime's `strategicAt` rule: the strategic market prices the station when it resolves it. */
  const linked = (stationId: string): boolean => {
    const m = deps.market?.() ?? null
    return m !== null && m.resolve(stationId) !== null
  }

  const options = (data: Parameters<FabWorldProvider>[0], now: number): StationWorld => {
    const reach = deps.reach()
    if (reach === null) return {}
    const c = deps.client()
    const remote = { data, world: fabRemoteWorld(reach, c?.systemView?.stations, reach.session.layout, data, now) }
    if (c === null) return { remote, linked, view: null }
    attach(c)
    keepView(c.systemView)
    const view = c.systemView
    const here = c.manifest?.systemId ?? null
    if (here === null) return { remote, linked, view }
    refreshOwn(c, here)
    const local = c.systemView !== null && c.systemView.systemId === here ? c.systemView : undefined
    const covered = new Set(ownSystems)
    covered.add(here)
    return {
      system: local,
      world: { world: { reach: reach.state, galaxy: reach.galaxy, views: [...views.values()], covered }, systemId: here },
      remote,
      linked,
      view,
    }
  }

  return {
    options,
    follow(): void {
      const c = deps.client()
      if (c !== null) attach(c)
    },
    dispose(): void {
      disposed = true
      offView?.()
      offView = null
      attached = null
    },
  }
}

let off: (() => void) | null = null
let source: FabWorldSource | null = null

const feature: GameFeature = {
  name: 'fabWorld',
  order: ORDER,
  init(ctx: GameContext): void {
    if (ctx.reach === null) return
    source = createFabWorldSource({
      reach: () => ctx.reach,
      client: () => ctx.services.maybe('strategic') ?? null,
      // The career runtime's market (one per client, `strategicMarketFor`), for the Repair tab's `linked` signal.
      market: () => {
        const c = ctx.services.maybe('strategic')
        return c === undefined ? null : strategicMarketFor(c)
      },
    })
    source.follow()
    off = registerFabWorld(source.options)
  },
  dispose(): void {
    off?.()
    off = null
    source?.dispose()
    source = null
  },
}

export default feature
